import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import { AdminCreateMarket, AdminFetchMarkets, AdminRenameMarket } from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type { CreateMarketPayload, Market, RenameMarketPayload } from 'src/interfaces/market'
import { normalizeApiListPayload } from 'src/utils/normalizeApiListPayload'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

interface FetchMarketsPayload {
  partnerId?: string
}

export function* onFetchMarkets(action: AsyncAction<FetchMarketsPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminFetchMarkets.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    // The endpoint answers with a raw array rather than `{ data, total }`; the normalizer
    // handles both, so this call site stays correct if it is ever wrapped.
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminFetchMarkets,
      {
        method: 'GET',
        query: { partnerId },
      },
    )

    const markets = normalizeApiListPayload<Market>(payload)

    yield put(AdminFetchMarkets.success(markets))

    if (action.next) {
      action.next(null, markets)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.market.onFetchMarkets] error' })

    yield put(AdminFetchMarkets.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load markets'))
    }
  }
}

export function* onCreateMarket(action: AsyncAction<CreateMarketPayload>) {
  try {
    const { payload }: { payload: Market } = yield call(
      callSecureApi,
      Endpoints.AdminCreateMarket,
      {
        method: 'POST',
        body: action.payload,
      },
    )

    yield put(AdminCreateMarket.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.market.onCreateMarket] error' })

    yield put(AdminCreateMarket.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to create market'))
    }
  }
}

export function* onRenameMarket(action: AsyncAction<RenameMarketPayload>) {
  const marketId = action.payload?.marketId

  if (!marketId) {
    yield put(AdminRenameMarket.failed(new Error('Missing marketId')))

    if (action.next) {
      action.next('Missing marketId')
    }

    return
  }

  try {
    // The body is built explicitly rather than forwarded: the backend handler reads only
    // `title` and silently discards anything else, so a stray `description` must never
    // reach it and appear to have been saved.
    const { payload }: { payload: Market } = yield call(
      callSecureApi,
      Endpoints.AdminRenameMarket,
      {
        method: 'PUT',
        params: [{ field: ':marketId', value: marketId }],
        body: { title: action.payload.title },
      },
    )

    yield put(AdminRenameMarket.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.market.onRenameMarket] error' })

    yield put(AdminRenameMarket.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to rename market'))
    }
  }
}

export function* marketSaga() {
  // A refresh or tab switch re-triggers the fetch, so only the newest run matters.
  yield takeLatest(AdminFetchMarkets.type.REQUEST, onFetchMarkets)
  // Mutations must each run.
  yield takeEvery(AdminCreateMarket.type.REQUEST, onCreateMarket)
  yield takeEvery(AdminRenameMarket.type.REQUEST, onRenameMarket)
}
